import React, { useEffect, useMemo, useState } from "react";
import mesApi from "../../../../Api/mes.api";
import {
  Ingredient,
  IngredientAlias,
  DeliveryLineDraft,
  Warehouse,
  WAREHOUSE_TYPE_MATERIAL,
} from "../../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import ImageCaptureField from "./ImageCaptureField";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";
import { isFlourIngredientType } from "../../../../utils/mesIngredientType";
import SearchableSelect, {
  SearchableSelectOption,
} from "../../../../components/DataEntry/SearchableSelect";

const RECIPIENT_SUPPLIER_PATTERN = /boogie\s*(bakery|pekara)/i;
const isRecipientSupplierName = (name?: string) =>
  Boolean(name?.trim() && RECIPIENT_SUPPLIER_PATTERN.test(name.trim()));

type ParsePhase = null | "delivery_note" | "lot_label";

const newLineKey = () => `line-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const numToStr = (v: number | null | undefined) =>
  v != null && !Number.isNaN(Number(v)) ? String(v) : "";

const pricingFromParsedLine = (l: Record<string, unknown>) => ({
  price_per_kg: numToStr(
    (l.cijena_po_kg ?? l.unit_price ?? l.cijena) as number | undefined
  ),
  vat_percent: numToStr((l.pdv_postotak ?? l.vat_percent) as number | undefined),
  discount_percent: numToStr((l.popust_postotak ?? l.discount_percent) as number | undefined),
  price_with_vat: numToStr((l.cijena_s_pdv ?? l.price_with_vat) as number | undefined),
});

const emptyLine = (): DeliveryLineDraft => ({
  key: newLineKey(),
  ingredient_id: 0,
  lot_number: "",
  quantity_kg: "",
  expiry_date: "",
  price_per_kg: "",
  vat_percent: "",
  discount_percent: "",
  price_with_vat: "",
  napomena: "",
});

// Matches the raw Supplier rows returned by GET /api/mes/suppliers (PascalCase).
interface SupplierOption {
  ID: number;
  Name: string;
  Oib?: string | null;
}

interface GoodsReceiptWizardProps {
  ingredients: Ingredient[];
  ingredientAliases?: IngredientAlias[];
  onClose: () => void;
  onSuccess: () => void;
}

const GoodsReceiptWizard: React.FC<GoodsReceiptWizardProps> = ({
  ingredients,
  ingredientAliases = [],
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<"mode" | "form" | "review">("mode");
  const [mode, setMode] = useState<"manual" | "scan">("manual");
  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState<ParsePhase>(null);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);

  const [brojOtpremnice, setBrojOtpremnice] = useState("");
  const [supplierId, setSupplierId] = useState(0);
  const [supplierName, setSupplierName] = useState("");
  const [supplierOib, setSupplierOib] = useState("");
  const [datumPrimitka, setDatumPrimitka] = useState(new Date().toISOString().split("T")[0]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehouseId, setWarehouseId] = useState(0);
  const [napomena, setNapomena] = useState("");
  const [deliveryNoteImage, setDeliveryNoteImage] = useState<string | undefined>();
  const [ocrRaw, setOcrRaw] = useState<unknown>();

  const [lines, setLines] = useState<DeliveryLineDraft[]>([emptyLine()]);
  const [lotScanLineKey, setLotScanLineKey] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    mesApi
      .getSuppliers({ is_active: true })
      .then((list) => setSuppliers(list.filter((s) => !isRecipientSupplierName(s.Name))))
      .catch(() => {});
  }, []);

  // Only material warehouses can hold raw stock, so those are the only ones a
  // receipt may book to. The server marks the one it would use by default.
  useEffect(() => {
    mesApi
      .getSkladista({ type: WAREHOUSE_TYPE_MATERIAL })
      .then((list) => {
        setWarehouses(list);
        setWarehouseId((prev) => prev || list.find((w) => w.is_default_receipt)?.warehouse_id || 0);
      })
      .catch(() => toast.error("Popis skladišta nije učitan"));
  }, []);

  const applyParseResult = (data: any) => {
    if (data.parsed?.broj_otpremnice) setBrojOtpremnice(data.parsed.broj_otpremnice);
    if (data.parsed?.datum) setDatumPrimitka(data.parsed.datum.split("T")[0]);

    if (data.supplier_rejected_recipient || data.supplier_warning) {
      setSupplierId(0);
      setSupplierName("");
      setSupplierOib("");
      toast.warn(
        data.supplier_warning ||
          "Prepoznat je primatelj (Boogie Bakery) — odaberite pravog dobavljača s otpremnice"
      );
    } else {
      if (data.suggested_supplier_id) setSupplierId(data.suggested_supplier_id);
      if (data.parsed?.dobavljac_naziv && !isRecipientSupplierName(data.parsed.dobavljac_naziv)) {
        setSupplierName(data.parsed.dobavljac_naziv);
      }
      if (data.parsed?.dobavljac_oib) setSupplierOib(data.parsed.dobavljac_oib);
    }

    setOcrRaw(data.ocr_raw_json);

    const parsedLines: DeliveryLineDraft[] = (data.lines || []).map((l: any) => ({
      key: newLineKey(),
      ingredient_id: l.suggested_ingredient_id || 0,
      extracted_name: l.naziv,
      lot_number: l.lot_broj || "",
      quantity_kg: l.kolicina_kg != null ? String(l.kolicina_kg) : "",
      expiry_date: l.rok_trajanja ? l.rok_trajanja.split("T")[0] : "",
      ...pricingFromParsedLine(l),
      napomena: "",
      ingredient_matches: l.ingredient_matches,
      match_confidence: l.match_confidence,
      needs_manual_ingredient: l.needs_manual_ingredient,
    }));

    if (parsedLines.length) setLines(parsedLines);
    setStep("form");
  };

  const handleScanDeliveryNote = async (base64: string) => {
    setDeliveryNoteImage(base64);
    try {
      setParsing("delivery_note");
      const data = await mesApi.parseDeliveryNote(base64);
      applyParseResult(data);
      toast.success("Otpremnica prepoznata — provjerite podatke");
    } catch (err: any) {
      toast.error(err.message || "Greška pri čitanju otpremnice");
    } finally {
      setParsing(null);
    }
  };

  const handleScanLotLabel = async (base64: string) => {
    if (!lotScanLineKey) return;
    try {
      setParsing("lot_label");
      const data = await mesApi.parseLotLabel(base64);
      if (data.lot_broj) {
        setLines((prev) =>
          prev.map((l) =>
            l.key === lotScanLineKey
              ? { ...l, lot_number: data.lot_broj, lot_label_image_base64: base64 }
              : l
          )
        );
        toast.success(`LOT: ${data.lot_broj}`);
      } else {
        setLines((prev) =>
          prev.map((l) =>
            l.key === lotScanLineKey ? { ...l, lot_label_image_base64: base64 } : l
          )
        );
        toast.warn("LOT nije prepoznat — unesite ručno");
      }
    } catch (err: any) {
      toast.error(err.message || "Greška pri čitanju etikete");
    } finally {
      setParsing(null);
      setLotScanLineKey(null);
    }
  };

  const updateLine = (key: string, patch: Partial<DeliveryLineDraft>) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  const removeLine = (key: string) => {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((l) => l.key !== key)));
  };

  const ingredientOptions: SearchableSelectOption[] = useMemo(
    () =>
      ingredients.map((ing) => ({
        value: ing.ID,
        label: ing.IngredientName,
        sublabel: ing.StandardCode || ing.RitamCode || undefined,
      })),
    [ingredients]
  );

  const aliasOptions: SearchableSelectOption[] = useMemo(
    () =>
      ingredientAliases.map((a) => ({
        value: a.ID,
        label: a.AliasName,
        sublabel: a.ExternalCode || undefined,
      })),
    [ingredientAliases]
  );

  const handleSelectRitamAlias = (key: string, aliasId: number) => {
    const alias = ingredientAliases.find((a) => a.ID === aliasId);
    setLines((prev) =>
      prev.map((l) =>
        l.key === key
          ? {
              ...l,
              ritam_alias_id: aliasId || undefined,
              ingredient_id: alias?.Ingredient ? alias.Ingredient : l.ingredient_id,
            }
          : l
      )
    );
  };

  const validateForm = (): boolean => {
    if (!brojOtpremnice.trim()) {
      toast.error("Unesite broj otpremnice");
      return false;
    }
    if (!supplierId && !supplierName.trim()) {
      toast.error("Odaberite ili unesite dobavljača");
      return false;
    }
    if (!warehouseId) {
      toast.error("Odaberite skladište na koje ide zaliha");
      return false;
    }
    const chosenName =
      supplierName.trim() || suppliers.find((s) => s.ID === supplierId)?.Name || "";
    if (isRecipientSupplierName(chosenName)) {
      toast.error("Boogie Bakery je primatelj — unesite dobavljača s otpremnice");
      return false;
    }
    for (const line of lines) {
      if (!line.ritam_alias_id) {
        toast.error("Svaka stavka mora imati odabranu sirovinu (Ritam)");
        return false;
      }
      if (!line.quantity_kg || parseFloat(line.quantity_kg) <= 0) {
        toast.error("Svaka stavka mora imati količinu (kg)");
        return false;
      }
    }
    return true;
  };

  const parseOptionalNum = (s: string) => {
    const t = s.trim();
    if (!t) return undefined;
    const n = parseFloat(t);
    return Number.isFinite(n) ? n : undefined;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitError(null);
    try {
      setLoading(true);
      await mesApi.createGoodsReceipt({
        delivery_note_number: brojOtpremnice.trim(),
        supplier_id: supplierId || undefined,
        supplier_name: supplierName.trim() || undefined,
        supplier_oib: supplierOib.trim() || undefined,
        received_date: datumPrimitka,
        warehouse_id: warehouseId,
        notes: napomena || undefined,
        delivery_note_image_base64: deliveryNoteImage,
        ocr_raw_json: ocrRaw,
        items: lines.map((l) => ({
          ingredient_id: l.ingredient_id || undefined,
          ritam_alias_id: l.ritam_alias_id || undefined,
          lot_number: l.lot_number || undefined,
          quantity_kg: parseFloat(l.quantity_kg),
          expiry_date: l.expiry_date || undefined,
          price_per_kg: parseOptionalNum(l.price_per_kg),
          vat_percent: parseOptionalNum(l.vat_percent),
          discount_percent: parseOptionalNum(l.discount_percent),
          price_with_vat: parseOptionalNum(l.price_with_vat),
          napomena: l.napomena || undefined,
          extracted_name: l.extracted_name,
          lot_label_image_base64: l.lot_label_image_base64,
        })),
      });
      const anyFlour = lines.some((l) => {
        const ing = ingredients.find((i) => i.ID === l.ingredient_id);
        return isFlourIngredientType(ing?.IngredientTypeName);
      });
      toast.success(
        anyFlour
          ? "Grupna dostava registrirana — brašno u karanteni (QC), ostalo odmah u zalihu."
          : "Grupna dostava registrirana."
      );
      onSuccess();
      onClose();
    } catch (err: any) {
      const msg = err.message || "Spremanje nije uspjelo";
      setSubmitError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const startMode = (m: "manual" | "scan") => {
    setMode(m);
    if (m === "manual") setStep("form");
    else setStep("form");
  };

  const parsingText =
    parsing === "delivery_note"
      ? "Čitanje otpremnice..."
      : parsing === "lot_label"
        ? "Čitanje LOT etikete..."
        : undefined;

  const busy = Boolean(parsing || loading);

  return (
    <div className="mes-modal-overlay delivery-wizard-overlay" onClick={onClose}>
      <ToastContainer position="top-center" autoClose={4000} style={{ zIndex: 10100 }} />
      <div
        className="mes-modal mes-modal-lg mes-modal-wide mes-modal-touch delivery-wizard-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {parsing === "delivery_note" && (
          <div className="delivery-parse-overlay" aria-busy="true">
            <LoadingSpinner loadingText={parsingText} />
          </div>
        )}
        <div className="modal-header">
          <div className="modal-title-group">
            <div className="modal-icon">📦</div>
            <div className="modal-title-text">
              <h3>Grupna dostava</h3>
              <p className="modal-subtitle">
                {step === "mode" && "Odaberite način unosa"}
                {step === "form" && (mode === "scan" ? "Pregled s otpremnice" : "Ručni unos stavki")}
                {step === "review" && "Pregled prije spremanja"}
              </p>
            </div>
          </div>
          <button type="button" className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body delivery-wizard-body">
          {submitError && (
            <div className="delivery-submit-error" role="alert">
              {submitError}
            </div>
          )}
          {step === "mode" && (
            <div className="delivery-mode-picker">
              <button type="button" className="delivery-mode-card" onClick={() => startMode("manual")}>
                <span className="delivery-mode-icon">✏️</span>
                <strong>Ručni unos</strong>
                <p>Dodajte stavke jednu po jednu</p>
              </button>
              <button type="button" className="delivery-mode-card" onClick={() => startMode("scan")}>
                <span className="delivery-mode-icon">📄</span>
                <strong>Skeniraj otpremnicu</strong>
                <p>Slikaj ili učitaj — AI prepoznaje stavke</p>
              </button>
            </div>
          )}

          {step === "form" && (
            <>
              {mode === "scan" && (
                <ImageCaptureField
                  label="Slika otpremnice"
                  onImage={handleScanDeliveryNote}
                  disabled={busy}
                />
              )}

              <div className="form-grid-2">
                <div className="form-group">
                  <label>
                    Broj otpremnice <span style={{ color: "#dc3545" }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={brojOtpremnice}
                    onChange={(e) => setBrojOtpremnice(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Datum primitka</label>
                  <input
                    type="date"
                    value={datumPrimitka}
                    onChange={(e) => setDatumPrimitka(e.target.value)}
                  />
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label>
                    Skladište <span style={{ color: "#dc3545" }}>*</span>
                  </label>
                  <select
                    value={warehouseId || ""}
                    onChange={(e) => setWarehouseId(Number(e.target.value))}
                  >
                    <option value="">-- Odaberi skladište --</option>
                    {warehouses.map((w) => (
                      <option key={w.warehouse_id} value={w.warehouse_id}>
                        {w.code} · {w.name}
                      </option>
                    ))}
                  </select>
                  <small className="form-hint">Zaliha se knjiži na odabrano skladište.</small>
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label>
                    Dobavljač <span style={{ color: "#dc3545" }}>*</span>
                  </label>
                  <select
                    value={supplierId}
                    onChange={(e) => {
                      const id = parseInt(e.target.value, 10);
                      setSupplierId(id);
                      const s = suppliers.find((x) => x.ID === id);
                      if (s) {
                        setSupplierName(s.Name);
                        setSupplierOib(s.Oib ?? "");
                      }
                    }}
                  >
                    <option value={0}>— Odaberi iz baze —</option>
                    {suppliers.map((s) => (
                      <option key={s.ID} value={s.ID}>
                        {s.Name}
                        {s.Oib ? ` (OIB: ${s.Oib})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Naziv dobavljača (s otpremnice)</label>
                  <input
                    type="text"
                    value={supplierName}
                    onChange={(e) => setSupplierName(e.target.value)}
                    placeholder="Ako nije u listi"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>OIB dobavljača</label>
                <input
                  type="text"
                  value={supplierOib}
                  onChange={(e) => setSupplierOib(e.target.value)}
                />
              </div>

              {mode === "manual" && (
                <ImageCaptureField
                  label="Slika otpremnice (opcionalno)"
                  onImage={setDeliveryNoteImage}
                  disabled={busy}
                />
              )}

              <div className="delivery-lines-header">
                <h4>Stavke</h4>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setLines((p) => [...p, emptyLine()])}
                >
                  + Dodaj stavku
                </button>
              </div>

              <div className="delivery-line-cards">
                {lines.map((line, index) => (
                  <div key={line.key} className="delivery-line-card">
                    <div className="delivery-line-card-header">
                      <span className="delivery-line-card-title">Stavka {index + 1}</span>
                      <button
                        type="button"
                        className="btn-link text-danger"
                        onClick={() => removeLine(line.key)}
                        disabled={lines.length <= 1}
                        aria-label="Ukloni stavku"
                      >
                        Ukloni
                      </button>
                    </div>
                    {line.extracted_name && (
                      <small className="extracted-name-hint" title="S otpremnice">
                        {line.extracted_name}
                        {line.match_confidence != null && (
                          <> ({Math.round(line.match_confidence * 100)}%)</>
                        )}
                      </small>
                    )}
                    <div className="form-group">
                      <label>
                        Sirovina (Ritam) <span className="field-required">*</span>
                      </label>
                      <SearchableSelect
                        value={line.ritam_alias_id ?? 0}
                        options={aliasOptions}
                        onChange={(val) => handleSelectRitamAlias(line.key, val)}
                        placeholder="Pretraži Ritam sirovine..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Sirovina</label>
                      <SearchableSelect
                        value={line.ingredient_id}
                        options={ingredientOptions}
                        onChange={(val) => updateLine(line.key, { ingredient_id: val })}
                        placeholder="Pretraži sirovine..."
                        className={line.needs_manual_ingredient ? "needs-review" : ""}
                      />
                    </div>
                    <div className="delivery-line-fields">
                      <div className="form-group">
                        <label>LOT</label>
                        <input
                          type="text"
                          value={line.lot_number}
                          onChange={(e) => updateLine(line.key, { lot_number: e.target.value })}
                          placeholder="LOT"
                        />
                        <button
                          type="button"
                          className="btn-secondary btn-sm lot-scan-btn"
                          onClick={() => setLotScanLineKey(line.key)}
                        >
                          📷 Scan LOT
                        </button>
                      </div>
                      <div className="form-group">
                        <label>
                          Qty (kg) <span className="field-required">*</span>
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          inputMode="decimal"
                          value={line.quantity_kg}
                          onChange={(e) => updateLine(line.key, { quantity_kg: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label>Expiry</label>
                        <input
                          type="date"
                          value={line.expiry_date}
                          onChange={(e) => updateLine(line.key, { expiry_date: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label>Price</label>
                        <input
                          type="number"
                          step="0.01"
                          inputMode="decimal"
                          value={line.price_per_kg}
                          onChange={(e) => updateLine(line.key, { price_per_kg: e.target.value })}
                          placeholder="€/kg"
                        />
                      </div>
                      <div className="form-group">
                        <label>VAT</label>
                        <input
                          type="number"
                          step="0.01"
                          inputMode="decimal"
                          value={line.vat_percent}
                          onChange={(e) => updateLine(line.key, { vat_percent: e.target.value })}
                          placeholder="%"
                        />
                      </div>
                      <div className="form-group">
                        <label>Discount</label>
                        <input
                          type="number"
                          step="0.01"
                          inputMode="decimal"
                          value={line.discount_percent}
                          onChange={(e) => updateLine(line.key, { discount_percent: e.target.value })}
                          placeholder="%"
                        />
                      </div>
                      <div className="form-group">
                        <label>Price with VAT</label>
                        <input
                          type="number"
                          step="0.01"
                          inputMode="decimal"
                          value={line.price_with_vat}
                          onChange={(e) => updateLine(line.key, { price_with_vat: e.target.value })}
                          placeholder="€"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="form-group delivery-notes-group">
                <label>Napomena</label>
                <textarea value={napomena} onChange={(e) => setNapomena(e.target.value)} rows={2} />
              </div>
            </>
          )}

          {step === "review" && (
            <div className="delivery-review">
              <p>
                <strong>{brojOtpremnice}</strong> — {supplierName || suppliers.find((s) => s.ID === supplierId)?.Name} —{" "}
                {lines.length} stavki
              </p>
              <p>
                Skladište:{" "}
                <strong>
                  {warehouses.find((w) => w.warehouse_id === warehouseId)?.name || "nije odabrano"}
                </strong>
              </p>
              <ul>
                {lines.map((l) => {
                  const ing = ingredients.find((i) => i.ID === l.ingredient_id);
                  const priceBits = [
                    l.price_per_kg && `Price ${l.price_per_kg}`,
                    l.vat_percent && `VAT ${l.vat_percent}%`,
                    l.discount_percent && `Disc. ${l.discount_percent}%`,
                    l.price_with_vat && `incl. VAT ${l.price_with_vat}`,
                  ].filter(Boolean);
                  return (
                    <li key={l.key}>
                      {ing?.IngredientName}: {l.quantity_kg} kg
                      {l.lot_number ? `, LOT ${l.lot_number}` : ""}
                      {l.expiry_date ? `, exp. ${l.expiry_date}` : ""}
                      {priceBits.length > 0 ? ` — ${priceBits.join(", ")}` : ""}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        {lotScanLineKey && (
          <div
            className="mes-modal-overlay lot-scan-overlay"
            onClick={() => !parsing && setLotScanLineKey(null)}
          >
            <div className="mes-modal delivery-wizard-modal" onClick={(e) => e.stopPropagation()}>
              {parsing === "lot_label" && (
                <div className="delivery-parse-overlay" aria-busy="true">
                  <LoadingSpinner loadingText={parsingText} />
                </div>
              )}
              <div className="modal-header">
                <h3>Slikaj LOT etiketu</h3>
                <button
                  type="button"
                  className="close-btn"
                  onClick={() => !parsing && setLotScanLineKey(null)}
                  disabled={Boolean(parsing)}
                >
                  ✕
                </button>
              </div>
              <div className="modal-body">
                <ImageCaptureField label="Etiketa / LOT" onImage={handleScanLotLabel} disabled={busy} />
              </div>
            </div>
          </div>
        )}

        <div className="modal-footer">
          {step !== "mode" && (
            <button
              type="button"
              className="btn-cancel"
              onClick={() => (step === "review" ? setStep("form") : setStep("mode"))}
            >
              Natrag
            </button>
          )}
          <button type="button" className="btn-cancel" onClick={onClose}>
            Odustani
          </button>
          {step === "form" && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setSubmitError(null);
                setStep("review");
              }}
              disabled={busy}
            >
              Pregled
            </button>
          )}
          {(step === "form" || step === "review") && (
            <button type="button" className="btn-save" onClick={handleSubmit} disabled={busy}>
              {loading ? "Spremanje..." : "Spremi grupnu dostavu"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoodsReceiptWizard;
